export interface Translations {
  // Header
  appTitle: string;
  appSubtitle: string;
  
  // Journey Planning
  planYourJourney: string;
  from: string;
  to: string;
  date: string;
  time: string;
  searchFerries: string;
  wherePlaceholder: string;
  
  // Stop name translations
  getStopName: (stopName: string) => string;
  getServiceType: (serviceType: string) => string;
  getOperator: (operator: string) => string;
  getWarningText: (warningKey: string) => string;
  
  // Route Results
  routesFound: string;
  noRoutesFound: string;
  departsIn: string;
  duration: string;
  searchingRoutes: string;
  adjustTime: string;
  
  // Warnings
  noBikes: string;
  noCars: string;
  noTrucks: string;
  noBikesCars: string;
  noCarsAndTrucks: string;
  noAllVehicles: string;
  museumClosureWarning: string;
  festivalWeekendException: string;
  counterPurchaseOnly: string;

    // Filter
    filter: string;
  active: string;
  reset: string;
  filterResults: string;
  capacityRequirements: string;
  vehiclesAllowed: string;
  bicyclesAllowed: string;
  hazardousMaterialsVehicles: string;
  serviceTypes: string;
  sortBy: string;
  departureTime: string;
  journeyDuration: string;
  arrivalTime: string;
  applyFilters: string;
  
  // Service Types
  ferry: string;
  highSpeedBoat: string;
  passengerBoat: string;
  passengerShip: string;
  triennaleEnhancedService: string;
  
  // Companies
  shikokuKisen: string;
  
  // Errors
  selectAll: string;
  selectDifferent: string;
  noFerriesFound: string;
  noDirectRoutes: string;
  errorSearching: string;
  
  // Bookmarks
  bookmark: string;
  bookmarked: string;
  addBookmark: string;
  removeBookmark: string;
  myBookmarks: string;
  viewBookmarks: string;
  saveRoute: string;
  savedRoutes: string;
  itinerary: string;
  myItineraries: string;
  newItinerary: string;
  addToItinerary: string;
  removeFromItinerary: string;
  exportBookmarks: string;
  importBookmarks: string;
  noBookmarks: string;
  bookmarkSaved: string;
  bookmarkRemoved: string;

  // Footer
  scheduleSubject: string;
  
  // Islands/Locations
  island: string;
  mainland: string;
  
  // Places
  takamatsu: string;
  uno: string;
  miyanoura: string;
  honmura: string;
  naoshima: string;
  ieura: string;
  teshima: string;
  inujima: string;
  karato: string;
  tonosho: string;
  shodoshima: string;
  kagawa: string;
  okayama: string;
}

export const translations: { [key: string]: Translations } = {
  en: {
    // Header
    appTitle: "Seto Ferry",
    appSubtitle: "Inland Sea Routes",
    
    // Journey Planning
    planYourJourney: "Plan Your Journey",
    from: "From",
    to: "To",
    date: "Date",
    time: "Time",
    searchFerries: "Search",
    wherePlaceholder: "where",
    
    // Stop name translations - English returns original
    getStopName: (stopName: string) => stopName,
    getServiceType: (serviceType: string) => serviceType,
    getOperator: (operator: string) => operator,
    getWarningText: (warningKey: string) => {
      const warnings: { [key: string]: string } = {
        'no_bikes': 'No bikes',
        'no_cars': 'No cars',
        'no_trucks': 'No trucks',
        'no_bikes_cars': 'No bikes, cars',
        'no_cars_trucks': 'No cars, trucks',
        'no_all_vehicles': 'No bikes, cars, trucks',
        'museum_closure': 'Not available when Teshima Art Museum and Inujima Seirensho Art Museum are closed.',
        'health_protocols': 'Health protocols required: mask, hand disinfection, no conversation onboard.',
        'commuter_rush': 'Crowded with commuters and students, suggest to choose earlier or later services.',
        'hazmat_only': 'For hazardous materials vehicles only, not available for passengers.',
        'hazardous_materials_first_tuesday': 'Reserved for hazardous materials transport on the first Tuesday of each month.'
      };
      return warnings[warningKey] || warningKey;
    },
    
    // Route Results
    routesFound: "route",
    noRoutesFound: "No routes found",
    departsIn: "to departure",
    duration: "Duration",
    searchingRoutes: "Searching for routes...",
    adjustTime: "Try adjusting your departure time or check if there are seasonal restrictions.",
    
    // Warnings
    noBikes: "No bikes",
    noCars: "No cars",
    noTrucks: "No trucks",
    noBikesCars: "No bikes, cars",
    noCarsAndTrucks: "No cars, trucks", 
    noAllVehicles: "No bikes, cars, trucks",
    museumClosureWarning: "Not available when Teshima Art Museum and Inujima Seirensho Art Museum are closed.",
    festivalWeekendException: "Festival weekend service only; no sailings on Apr 29.",
    counterPurchaseOnly: "Not available for online reservation. Purchasable at ticket counter during business hours.",
    
    // Filter
    filter: "Filter",
    active: "Active",
    reset: "Reset",
    filterResults: "Filter Results",
    capacityRequirements: "Capacity Requirements",
    vehiclesAllowed: "Vehicles allowed",
    bicyclesAllowed: "Bicycles allowed",
    hazardousMaterialsVehicles: "Hazardous materials vehicles",
    serviceTypes: "Service Types",
    sortBy: "Sort By",
    departureTime: "Departure Time",
    journeyDuration: "Journey Duration",
    arrivalTime: "Arrival Time",
    applyFilters: "Apply Filters",
    
    // Service Types
    ferry: "Ferry",
    highSpeedBoat: "High Speed Boat",
    passengerBoat: "Passenger Boat",
    passengerShip: "Passenger ship",
    triennaleEnhancedService: "Triennale Enhanced Service",
    
    // Companies
    shikokuKisen: "Shikoku Kisen Co., Ltd.",
    
    // Bookmarks
    bookmark: "Bookmark",
    bookmarked: "Bookmarked",
    addBookmark: "Add to bookmarks",
    removeBookmark: "Remove bookmark",
    myBookmarks: "My Bookmarks",
    viewBookmarks: "View Bookmarks",
    saveRoute: "Save Route",
    savedRoutes: "Saved Routes",
    itinerary: "Itinerary",
    myItineraries: "My Itineraries",
    newItinerary: "New Itinerary",
    addToItinerary: "Add to Itinerary",
    removeFromItinerary: "Remove from Itinerary",
    exportBookmarks: "Export Bookmarks",
    importBookmarks: "Import Bookmarks",
    noBookmarks: "No bookmarks yet. Save routes you'd like to remember!",
    bookmarkSaved: "Route bookmarked",
    bookmarkRemoved: "Bookmark removed",

    // Errors
    selectAll: "Please select departure stop, destination, date, and time.",
    selectDifferent: "Please select different departure and destination stops.",
    noFerriesFound: "No ferries found for the selected time. Try an earlier time or different date.",
    noDirectRoutes: "No direct routes available between these stops.",
    errorSearching: "Error searching for routes. Please try again.",
    
    // Footer
    scheduleSubject: "Ferry schedules subject to weather and seasonal changes",
    
    // Islands/Locations
    island: "Island",
    mainland: "Mainland",
    
    // Places
    takamatsu: "Takamatsu",
    uno: "Uno",
    miyanoura: "Miyanoura",
    honmura: "Honmura", 
    naoshima: "Naoshima",
    ieura: "Ieura",
    teshima: "Teshima",
    inujima: "Inujima",
    karato: "Karato",
    tonosho: "Tonosho",
    shodoshima: "Shodoshima",
    kagawa: "Kagawa",
    okayama: "Okayama",
  },
  
  zh: {
    // Header
    appTitle: "濑户内海轮渡",
    appSubtitle: "内海航线",
    
    // Journey Planning
    planYourJourney: "规划您的行程",
    from: "出发地",
    to: "目的地", 
    date: "日期",
    time: "时间",
    searchFerries: "搜索",
    wherePlaceholder: "选择地点",
    
    // Stop name translations - Chinese mappings
    getStopName: (stopName: string) => {
      const translations: { [key: string]: string } = {
        "Takamatsu": "高松",
        "Takamatsu Higashi": "高松东港",
        "Uno": "宇野",
        "Miyanoura (Naoshima)": "宫浦 (直岛)",
        "Honmura (Naoshima)": "本村 (直岛)",
        "Ieura (Teshima)": "家浦 (丰岛)",
        "Karato (Teshima)": "唐柜 (丰岛)",
        "Tonosho (Shodoshima)": "土庄 (小豆岛)",
        "Ikeda (Shodoshima)": "池田 (小豆岛)",
        "Sakate (Shodoshima)": "坂手 (小豆岛)",
        "Oshima": "大岛",
        "Hoden": "宝传",
        "Kyobashi": "京桥",
        "Shin-Okayama": "新冈山",
        "Kobe": "神户",
        "Inujima": "犬岛",
        "Megijima": "女木岛",
        "Ogijima": "男木岛",
        "Himeji": "姫路",
        "Fukuda (Shodoshima)": "福田 (小豆岛)"
      };
      return translations[stopName] || stopName;
    },
    getServiceType: (serviceType: string) => {
      const translations: { [key: string]: string } = {
        "Ferry": "渡轮",
        "High Speed Boat": "高速船",
        "Passenger Boat": "客船", 
        "Passenger ship": "客船",
        "Setouchi Triennale 2025": "濑户内国际艺术节2025"
      };
      return translations[serviceType] || serviceType;
    },
    getOperator: (operator: string) => {
      const translations: { [key: string]: string } = {
        "Shikoku Kisen Co., Ltd.": "四国汽船株式会社",
        "Teshima Ferry Company": "丰岛渡轮株式会社",
        "Shodoshima Teshima Ferry Company": "小豆岛丰岛渡轮株式会社",
        "Shiyujima Kaiun Company": "雌雄岛海运株式会社",
        "International Ryobi Ferry": "国际两备渡轮",
        "Oshima Seishoen": "大岛青松园",
        "Akebono Maritime Company": "曙光海运株式会社",
        "Shodoshima Ferry & Kokusai Ryobi Ferry": "小豆岛渡轮・国际两备渡轮",
        "Okayama Kyobashi Cruise Company": "冈山京桥游船公司",
        "Shodoshima Kyuko Ferry": "小豆岛急行渡轮",
        "Shodoshima Ferry Co., Ltd.": "小豆岛渡轮株式会社",
        "Shodoshima Ferry": "小豆岛渡轮",
        "Jumbo Ferry": "Jumbo渡轮"
      };
      return translations[operator] || operator;
    },
    getWarningText: (warningKey: string) => {
      const warnings: { [key: string]: string } = {
        'no_bikes': '不允许自行车',
        'no_cars': '不允许汽车',
        'no_trucks': '不允许卡车',
        'no_bikes_cars': '不允许自行车、汽车',
        'no_cars_trucks': '不允许汽车、卡车',
        'no_all_vehicles': '不允许自行车、汽车、卡车',
        'museum_closure': '丰岛美术馆和犬岛精炼所美术馆关闭时不运营。',
        'health_protocols': '健康防护要求：佩戴口罩、手部消毒、船上禁止交谈。',
        'commuter_rush': '通勤高峰时段，客流较大，建议选择其他班次。',
        'hazmat_only': '仅供危险品车辆使用，不对乘客开放。',
        'hazardous_materials_first_tuesday': '每月第一个周二仅供危险品运输使用。'
      };
      return warnings[warningKey] || warningKey;
    },
    
    // Route Results
    routesFound: "条航线",
    noRoutesFound: "未找到航线",
    departsIn: "后出发",
    duration: "航行时间",
    searchingRoutes: "搜索航线中...",
    adjustTime: "请尝试调整出发时间或检查是否有季节性限制。",
    
    // Warnings
    noBikes: "不可携带自行车",
    noCars: "不可携带汽车", 
    noTrucks: "不可携带卡车",
    noBikesCars: "不可携带自行车、汽车",
    noCarsAndTrucks: "不可携带汽车、卡车",
    noAllVehicles: "不可携带自行车、汽车、卡车",
    museumClosureWarning: "丰岛美术馆和犬岛精炼所美术馆关闭时不运营。",
    festivalWeekendException: "仅在节庆周末运营；4月29日停航。",
    counterPurchaseOnly: "无法线上预约，仅限营业时间于售票窗口购买。",
    
    // Filter
    filter: "筛选",
    active: "已启用",
    reset: "重置",
    filterResults: "筛选结果",
    capacityRequirements: "载客要求",
    vehiclesAllowed: "允许携带车辆",
    bicyclesAllowed: "允许携带自行车", 
    hazardousMaterialsVehicles: "危险品车辆",
    serviceTypes: "船舶类型",
    sortBy: "排序方式",
    departureTime: "出发时间",
    journeyDuration: "航行时间",
    arrivalTime: "到达时间",
    applyFilters: "应用筛选",
    
    // Service Types
    ferry: "汽车渡轮",
    highSpeedBoat: "高速船",
    passengerBoat: "客船",
    passengerShip: "客轮",
    triennaleEnhancedService: "艺术祭加班船",
    
    // Companies
    shikokuKisen: "四国汽船株式会社",
    
    // Bookmarks
    bookmark: "收藏",
    bookmarked: "已收藏",
    addBookmark: "添加到收藏",
    removeBookmark: "取消收藏",
    myBookmarks: "我的收藏",
    viewBookmarks: "查看收藏",
    saveRoute: "保存路线",
    savedRoutes: "已保存路线",
    itinerary: "行程",
    myItineraries: "我的行程",
    newItinerary: "新建行程",
    addToItinerary: "添加到行程",
    removeFromItinerary: "从行程中移除",
    exportBookmarks: "导出收藏",
    importBookmarks: "导入收藏",
    noBookmarks: "暂无收藏。保存您想要记住的路线！",
    bookmarkSaved: "路线已收藏",
    bookmarkRemoved: "收藏已移除",
    
    // Errors
    selectAll: "请选择出发地、目的地、日期和时间。",
    selectDifferent: "请选择不同的出发地和目的地。", 
    noFerriesFound: "所选时间无轮渡。请尝试更早的时间或其他日期。",
    noDirectRoutes: "这些地点之间没有直达航线。",
    errorSearching: "搜索航线时出错，请重试。",
    
    // Footer
    scheduleSubject: "轮渡时刻表可能因天气和季节变化而调整",
    
    // Islands/Locations
    island: "岛屿",
    mainland: "本土",
    
    // Places
    takamatsu: "高松",
    uno: "宇野",
    miyanoura: "宫浦",
    honmura: "本村",
    naoshima: "直岛", 
    ieura: "家浦",
    teshima: "丰岛",
    inujima: "犬岛",
    karato: "唐柜",
    tonosho: "土庄",
    shodoshima: "小豆岛",
    kagawa: "香川",
    okayama: "冈山",
  },

  ja: {
    // Header
    appTitle: "瀬戸内海フェリー",
    appSubtitle: "内海航路",

    // Journey Planning
    planYourJourney: "旅程を計画する",
    from: "出発地",
    to: "目的地",
    date: "日付",
    time: "時刻",
    searchFerries: "検索",
    wherePlaceholder: "場所を選択",

    // Stop name translations - Japanese mappings
    getStopName: (stopName: string) => {
      const translations: { [key: string]: string } = {
        "Takamatsu": "高松",
        "Takamatsu Higashi": "高松東港",
        "Uno": "宇野",
        "Miyanoura (Naoshima)": "宮浦（直島）",
        "Honmura (Naoshima)": "本村（直島）",
        "Ieura (Teshima)": "家浦（豊島）",
        "Karato (Teshima)": "唐櫃（豊島）",
        "Tonosho (Shodoshima)": "土庄（小豆島）",
        "Ikeda (Shodoshima)": "池田（小豆島）",
        "Sakate (Shodoshima)": "坂手（小豆島）",
        "Oshima": "大島",
        "Hoden": "宝伝",
        "Kyobashi": "京橋",
        "Shin-Okayama": "新岡山",
        "Kobe": "神戸",
        "Inujima": "犬島",
        "Megijima": "女木島",
        "Ogijima": "男木島",
        "Himeji": "姫路",
        "Fukuda (Shodoshima)": "福田（小豆島）"
      };
      return translations[stopName] || stopName;
    },
    getServiceType: (serviceType: string) => {
      const translations: { [key: string]: string } = {
        "Ferry": "フェリー",
        "High Speed Boat": "高速船",
        "Passenger Boat": "旅客船",
        "Passenger ship": "旅客船",
        "Setouchi Triennale 2025": "瀬戸内国際芸術祭2025"
      };
      return translations[serviceType] || serviceType;
    },
    getOperator: (operator: string) => {
      const translations: { [key: string]: string } = {
        "Shikoku Kisen Co., Ltd.": "四国汽船株式会社",
        "Teshima Ferry Company": "豊島フェリー株式会社",
        "Shodoshima Teshima Ferry Company": "小豆島豊島フェリー株式会社",
        "Shiyujima Kaiun Company": "雌雄島海運株式会社",
        "International Ryobi Ferry": "国際両備フェリー",
        "Oshima Seishoen": "大島青松園",
        "Akebono Maritime Company": "あけぼの海運株式会社",
        "Shodoshima Ferry & Kokusai Ryobi Ferry": "小豆島フェリー・国際両備フェリー",
        "Okayama Kyobashi Cruise Company": "岡山京橋クルーズ会社",
        "Shodoshima Kyuko Ferry": "小豆島急行フェリー",
        "Shodoshima Ferry Co., Ltd.": "小豆島フェリー株式会社",
        "Shodoshima Ferry": "小豆島フェリー",
        "Jumbo Ferry": "ジャンボフェリー"
      };
      return translations[operator] || operator;
    },
    getWarningText: (warningKey: string) => {
      const warnings: { [key: string]: string } = {
        'no_bikes': '自転車不可',
        'no_cars': '自動車不可',
        'no_trucks': 'トラック不可',
        'no_bikes_cars': '自転車・自動車不可',
        'no_cars_trucks': '自動車・トラック不可',
        'no_all_vehicles': '自転車・自動車・トラック不可',
        'museum_closure': '豊島美術館と犬島精錬所美術館が閉館時は運休。',
        'health_protocols': '健康対策が必要：マスク着用、手指消毒、船内での会話禁止。',
        'commuter_rush': '通勤・通学時間帯で混雑します。早い時間または遅い時間の便をご検討ください。',
        'hazmat_only': '危険物車両専用、一般乗客は利用できません。',
        'hazardous_materials_first_tuesday': '毎月第1火曜日は危険物輸送専用となります。'
      };
      return warnings[warningKey] || warningKey;
    },

    // Route Results
    routesFound: "航路",
    noRoutesFound: "航路が見つかりません",
    departsIn: "後出発",
    duration: "所要時間",
    searchingRoutes: "航路を検索中...",
    adjustTime: "出発時刻を調整するか、季節的な制限がないかご確認ください。",

    // Warnings
    noBikes: "自転車持込不可",
    noCars: "自動車持込不可",
    noTrucks: "トラック持込不可",
    noBikesCars: "自転車・自動車持込不可",
    noCarsAndTrucks: "自動車・トラック持込不可",
    noAllVehicles: "自転車・自動車・トラック持込不可",
    museumClosureWarning: "豊島美術館と犬島精錬所美術館が閉館時は運休。",
    festivalWeekendException: "芸術祭期間中の週末のみ運航。4月29日は運休。",
    counterPurchaseOnly: "オンライン予約不可。営業時間内に窓口でのみ購入可能。",

    // Filter
    filter: "フィルター",
    active: "有効",
    reset: "リセット",
    filterResults: "検索結果を絞り込む",
    capacityRequirements: "収容要件",
    vehiclesAllowed: "車両持込可",
    bicyclesAllowed: "自転車持込可",
    hazardousMaterialsVehicles: "危険物車両",
    serviceTypes: "船舶タイプ",
    sortBy: "並び替え",
    departureTime: "出発時刻",
    journeyDuration: "所要時間",
    arrivalTime: "到着時刻",
    applyFilters: "フィルターを適用",

    // Service Types
    ferry: "カーフェリー",
    highSpeedBoat: "高速船",
    passengerBoat: "旅客船",
    passengerShip: "旅客船",
    triennaleEnhancedService: "芸術祭増便",

    // Companies
    shikokuKisen: "四国汽船株式会社",

    // Bookmarks
    bookmark: "ブックマーク",
    bookmarked: "保存済み",
    addBookmark: "ブックマークに追加",
    removeBookmark: "ブックマーク削除",
    myBookmarks: "マイブックマーク",
    viewBookmarks: "ブックマークを表示",
    saveRoute: "航路を保存",
    savedRoutes: "保存済み航路",
    itinerary: "旅程",
    myItineraries: "マイ旅程",
    newItinerary: "新しい旅程",
    addToItinerary: "旅程に追加",
    removeFromItinerary: "旅程から削除",
    exportBookmarks: "ブックマークをエクスポート",
    importBookmarks: "ブックマークをインポート",
    noBookmarks: "まだブックマークがありません。覚えておきたい航路を保存してください！",
    bookmarkSaved: "航路を保存しました",
    bookmarkRemoved: "ブックマークを削除しました",

    // Errors
    selectAll: "出発地、目的地、日付、時刻を選択してください。",
    selectDifferent: "異なる出発地と目的地を選択してください。",
    noFerriesFound: "選択した時刻にフェリーが見つかりません。早い時刻または別の日付をお試しください。",
    noDirectRoutes: "これらの停留所間に直行航路はありません。",
    errorSearching: "航路検索中にエラーが発生しました。もう一度お試しください。",

    // Footer
    scheduleSubject: "フェリーの運航スケジュールは天候や季節により変更される場合があります",

    // Islands/Locations
    island: "島",
    mainland: "本土",

    // Places
    takamatsu: "高松",
    uno: "宇野",
    miyanoura: "宮浦",
    honmura: "本村",
    naoshima: "直島",
    ieura: "家浦",
    teshima: "豊島",
    inujima: "犬島",
    karato: "唐櫃",
    tonosho: "土庄",
    shodoshima: "小豆島",
    kagawa: "香川",
    okayama: "岡山",
  }
};
